import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SignatureAdopt from "../onboarding/SignatureAdopt.jsx";
import ClientDocRenderer from "../clientsign/ClientDocRenderer.jsx";
import { fmtTime, BRAND, GOLD } from "../onboarding/helpers.js";

// Public signing page for clients and family representatives (no portal login).
const API = "/api/client-sign";
async function call(path, opts = {}) {
  const res = await fetch(API + path, { credentials: "include", headers: { "Content-Type": "application/json" }, ...opts, body: opts.body ? JSON.stringify(opts.body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.error || "Something went wrong"); e.status = res.status; e.data = data; throw e; }
  return data;
}

const ESIGN = [
  "Jybek HomeCare Services (Feel At Home Care LLC) would like to provide these documents electronically and have you sign them electronically.",
  "By agreeing, you consent to receive these records electronically and to use electronic signatures and initials, which have the same legal effect as handwritten ones under the federal ESIGN Act and New Hampshire’s Uniform Electronic Transactions Act (RSA 294-E).",
  "You may ask for paper copies at any time at no charge by calling 888-717-5009. You need a device with a web browser to view and keep these records; your signed copies can be downloaded here once Jybek has approved them.",
];

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/logo.png" alt="Jybek HomeCare Services" className="h-10" />
          <div><div className="font-bold leading-tight" style={{ color: "#1B3A6B" }}>Jybek HomeCare Services</div><div className="text-xs text-gray-500">Secure document signing</div></div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6">{children}</main>
    </div>
  );
}
const Card = ({ children }) => <div className="bg-white border rounded-2xl shadow-sm p-5 sm:p-6 space-y-4">{children}</div>;

function DocView({ docKey, onBack, onNext }) {
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  const [missing, setMissing] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => call(`/session/docs/${docKey}`).then((d) => { setP(d); setMissing(null); }).catch((e) => setError(e.message));
  useEffect(() => { setP(null); load(); window.scrollTo(0, 0); }, [docKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return <div className="text-gray-500">{error || "Loading…"}</div>;

  const onChange = async (values) => {
    setP((cur) => ({ ...cur, data: { ...cur.data, ...values } }));
    try { const r = await call(`/session/docs/${docKey}`, { method: "PUT", body: values }); setP((cur) => ({ ...cur, data: r.data, stamps: r.stamps, missing: r.missing })); }
    catch (e) { setError(e.message); }
  };
  const onStamp = async (key) => {
    setBusyKey(key); setError("");
    try { const r = await call(`/session/docs/${docKey}/stamp`, { method: "POST", body: { key } }); setP((cur) => ({ ...cur, data: { ...cur.data, [key]: r.stamp }, missing: r.missing })); }
    catch (e) { setError(e.message); } finally { setBusyKey(null); }
  };
  const submit = async () => {
    setBusy(true); setError(""); setMissing(null);
    try { const r = await call(`/session/docs/${docKey}/submit`, { method: "POST" }); onNext(p.next_key, r.all_done); }
    catch (e) { if (e.data?.missing) setMissing(e.data.missing); else setError(e.message); } finally { setBusy(false); }
  };
  const t = p.template;
  return (
    <div className="pb-24">
      <button onClick={onBack} className="text-sm hover:underline" style={{ color: BRAND }}>← All documents</button>
      <div className="bg-white border rounded-2xl shadow-sm mt-3">
        <div className="px-5 sm:px-7 pt-6 pb-4 border-b-2" style={{ borderColor: "#C9A84C" }}>
          <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>{t.title}</h1>
          {t.sub && <p className="text-sm italic text-gray-500 mt-1">{t.sub}</p>}
          {p.locked && <div className="mt-2 text-xs inline-block px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">✓ Completed {fmtTime(p.signer_signed_at)}</div>}
        </div>
        <div className="px-5 sm:px-7 py-6">
          <ClientDocRenderer template={t} data={p.data} role="signer" locked={p.locked} images={p.images} stamps={p.stamps} signerRole={p.signer_role} onChange={onChange} onStamp={onStamp} busyKey={busyKey} />
        </div>
      </div>
      {error && <div className="mt-4 text-sm bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3">{error}</div>}
      {missing && <div className="mt-4 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-3"><div className="font-semibold text-amber-900 mb-1">Still needed:</div><ul className="list-disc ml-5 text-amber-900">{missing.map((m) => <li key={m}>{m}</li>)}</ul></div>}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t shadow-lg px-4 py-3 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <span className="text-xs text-gray-600">{p.locked ? "Completed" : p.missing.length ? `${p.missing.length} item${p.missing.length === 1 ? "" : "s"} left` : "Ready to finish"}</span>
          {p.locked ? (p.next_key ? <button onClick={() => onNext(p.next_key)} className="px-5 py-2.5 rounded-lg text-white font-semibold" style={{ backgroundColor: BRAND }}>Next document →</button> : <button onClick={onBack} className="px-5 py-2.5 rounded-lg text-white font-semibold" style={{ backgroundColor: BRAND }}>Done</button>)
            : <button onClick={submit} disabled={busy} className="px-5 py-2.5 rounded-lg text-white font-semibold disabled:opacity-60" style={{ backgroundColor: BRAND }}>{busy ? "Saving…" : "Finish this document"}</button>}
        </div>
      </div>
    </div>
  );
}

export default function ClientSign() {
  const { token } = useParams();
  const [stage, setStage] = useState("loading");
  const [link, setLink] = useState(null);
  const [s, setS] = useState(null);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const [agree, setAgree] = useState(false);
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSession = () => call("/session").then((d) => { setS(d); setStage("session"); }).catch((e) => { if (e.status === 401) setStage("code"); else { setError(e.message); setStage("error"); } });
  useEffect(() => {
    call(`/link/${token}`).then((l) => { setLink(l); loadSession(); }).catch((e) => { setError(e.message); setStage("error"); });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (stage === "loading") return <Shell><div className="text-gray-500">Loading…</div></Shell>;
  if (stage === "error") return <Shell><Card><h1 className="text-xl font-bold" style={{ color: BRAND }}>We couldn't open these documents</h1><p className="text-sm text-gray-700">{error}</p></Card></Shell>;

  if (stage === "code") return (
    <Shell>
      <Card>
        <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>Hello{link?.signer_first_name ? `, ${link.signer_first_name}` : ""}</h1>
        <p className="text-sm text-gray-700">Jybek HomeCare Services has sent you {link?.kind === "discharge" ? "a discharge notice" : "client documents"} to review and sign. To protect your information, enter the 6-digit access code we sent you{link?.has_phone ? " by text message" : ""} or gave you by phone.</p>
        <form onSubmit={async (e) => { e.preventDefault(); setBusy(true); setError(""); try { await call(`/link/${token}/verify`, { method: "POST", body: { code } }); await loadSession(); } catch (err) { setError(err.message); } finally { setBusy(false); } }} className="space-y-3">
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" className="w-full text-center text-2xl tracking-[0.4em] border rounded-lg px-3 py-3" />
          {error && <div className="text-sm text-red-700">{error}</div>}
          <button disabled={busy || code.length !== 6} className="w-full py-3 rounded-lg text-white font-semibold disabled:opacity-50" style={{ backgroundColor: BRAND }}>{busy ? "Checking…" : "Continue"}</button>
        </form>
        <p className="text-xs text-gray-500">Didn't get a code? Call Jybek HomeCare Services at 888-717-5009.</p>
      </Card>
    </Shell>
  );

  const r = s.request;
  if (r.status === "paper_requested") return <Shell><Card><h1 className="text-xl font-bold" style={{ color: BRAND }}>Paper copies requested</h1><p className="text-sm">Jybek HomeCare Services will contact you with paper copies. Questions: 888-717-5009.</p><button className="text-sm underline" style={{ color: BRAND }} onClick={async () => { await call("/session/consent", { method: "POST", body: { agree: true } }); loadSession(); }}>Sign electronically instead</button></Card></Shell>;

  if (!r.consent_at) return (
    <Shell>
      <Card>
        <div className="text-xs font-bold tracking-[0.15em]" style={{ color: GOLD }}>STEP 1 OF 2 · BEFORE YOU BEGIN</div>
        <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>Consent to electronic records and signatures</h1>
        <p className="text-sm text-gray-700">Documents for <b>{s.client_name}</b>, signed by <b>{r.signer_name}</b> ({s.signer_role}).</p>
        {ESIGN.map((t) => <p key={t} className="text-sm text-gray-700">{t}</p>)}
        <label className="flex items-start gap-2 text-sm font-medium"><input type="checkbox" className="mt-1" checked={agree} onChange={(e) => setAgree(e.target.checked)} /><span>I agree to use electronic records and electronic signatures for these documents.</span></label>
        <div className="flex flex-col sm:flex-row gap-3">
          <button disabled={!agree} onClick={async () => { await call("/session/consent", { method: "POST", body: { agree: true } }); loadSession(); }} className="px-6 py-3 rounded-lg text-white font-semibold disabled:opacity-50" style={{ backgroundColor: BRAND }}>I agree — continue</button>
          <button onClick={async () => { if (window.confirm("Request paper copies instead?")) { await call("/session/consent", { method: "POST", body: { paper: true } }); loadSession(); } }} className="px-6 py-3 rounded-lg border font-semibold text-gray-700">I prefer paper copies</button>
        </div>
      </Card>
    </Shell>
  );

  if (!r.adopted) return (
    <Shell>
      <Card>
        <div className="text-xs font-bold tracking-[0.15em]" style={{ color: GOLD }}>STEP 2 OF 2 · YOUR SIGNATURE</div>
        <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>Your signature and initials</h1>
        <SignatureAdopt defaultName={r.signer_name} onAdopt={async (b) => { await call("/session/adopt", { method: "POST", body: b }); await loadSession(); }} />
      </Card>
    </Shell>
  );

  if (doc) return <Shell><DocView docKey={doc} onBack={() => { setDoc(null); loadSession(); }} onNext={(next, allDone) => { if (next && !allDone) setDoc(next); else { setDoc(null); loadSession(); } }} /></Shell>;

  const done = s.docs.filter((d) => d.signer_status === "signed").length;
  const next = s.docs.find((d) => d.signer_status !== "signed" && !d.waiting_agency);
  return (
    <Shell>
      <div className="space-y-4">
        <section className="rounded-2xl p-6 text-white shadow" style={{ background: "linear-gradient(120deg,#0f3d73,#154E93 58%,#2870b8)" }}>
          <div className="text-blue-100 text-xs tracking-[0.18em] font-semibold">{r.kind === "discharge" ? "DISCHARGE NOTICE" : "CLIENT DOCUMENTS"}</div>
          <h1 className="text-2xl font-bold mt-1">{s.client_name}</h1>
          <p className="text-blue-100 text-sm">Signing as {r.adopted_name || r.signer_name} · {s.signer_role}</p>
          <p className="text-sm mt-3">{done} of {s.docs.length} completed</p>
        </section>
        {r.status === "approved" && <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800"><b>All set.</b> Jybek HomeCare Services approved your documents on {fmtTime(r.approved_at)}. Download your signed copies below.</div>}
        {r.status === "signed" && <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800"><b>Thank you — everything is signed.</b> Jybek HomeCare Services will review the documents. Your signed copies will be available here once approved.</div>}
        {next && <button onClick={() => setDoc(next.key)} className="w-full flex items-center justify-between rounded-xl p-4 text-white font-semibold shadow" style={{ backgroundColor: BRAND }}><span>{done ? "Continue" : "Start"}: {next.title}</span><span>→</span></button>}
        <div className="bg-white border rounded-xl divide-y">
          {s.docs.map((d) => (
            <div key={d.key} className="flex items-center gap-3 px-4 py-3">
              <span className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${d.signer_status === "signed" ? "bg-green-600 text-white" : "border-2 border-gray-300"}`}>{d.signer_status === "signed" ? "✓" : ""}</span>
              <button onClick={() => !d.waiting_agency && setDoc(d.key)} className="flex-1 text-left">
                <div className="text-sm font-medium text-gray-800">{d.title}</div>
                <div className="text-xs text-gray-500">{d.waiting_agency ? "Being prepared by Jybek" : d.signer_status === "signed" ? `Completed ${fmtTime(d.signer_signed_at)}` : d.sub}</div>
              </button>
              {d.has_copy && <a href={`${API}/session/copies/${d.key}`} className="text-xs font-semibold underline" style={{ color: BRAND }}>Download</a>}
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 text-center">Questions? Call Jybek HomeCare Services at 888-717-5009.</p>
      </div>
    </Shell>
  );
}
